import React, { useContext, useState, useCallback, useEffect } from 'react';
import { useHistory } from "react-router-dom";
import { Button, Col, Row, Table, Badge, Alert } from "react-bootstrap";
import { Users } from "react-feather";
import TableComponent from "../../components/widgets/table/index";
import { useQuery, useQueryClient } from "react-query";
import { ModalContext } from 'context/ModalContext';
import AddProductUnderwriter from 'components/widgets/modals/AddProductUnderwriter';
import { fetchConfiguredUnderwriters, listMyRequests, listMyUnderwriters } from 'services/apiSrv';
import AddPanelRequest from 'components/widgets/modals/AddPanelRequest';
import AddDmvic from 'components/widgets/modals/AddDmvic';
import Dmvic_Credential from 'components/widgets/forms/setup/Dmvic_Credential';
import { getDmvicConfig, removeDmvicMember } from 'services/dvmvic';
import { Drawer, Space } from 'antd';

function Underwriter() {
  const history = useHistory();
  const { setModalShow, modalShow, modalClose } = useContext(ModalContext);
  const queryClient = useQueryClient();

  // Drawer states
  const [dmvicDrawerOpen, setDmvicDrawerOpen] = useState(false);
  const [credentialsDrawerOpen, setCredentialsDrawerOpen] = useState(false);

  // Other modals
  const [panelModalShow, setPanelModalShow] = useState(false);
  const [underwriterModalShow, setUnderwriterModalShow] = useState(false);

  const handlePanelModalClose = useCallback(() => setPanelModalShow(false), []);
  const handleUnderwriterModalClose = useCallback(() => setUnderwriterModalShow(false), []);

  // ─── DMVIC Config ──────────────────────────────────────────────
  const {
    data: dmvicConfig = null,
    isLoading: loadingDmvic,
    refetch: refetchDmvic,
  } = useQuery({
    queryKey: ["dmvic-config"],
    queryFn: async () => {
      const res = await getDmvicConfig();
      return res.data || null;
    },
    staleTime: 10 * 60 * 1000,
    retry: 2,
  });

  const [localMembers, setLocalMembers] = useState([]);
  const [deletingMemberRef, setDeletingMemberRef] = useState(null);

  useEffect(() => {
    if (dmvicConfig?.members) {
      setLocalMembers(dmvicConfig.members);
    }
  }, [dmvicConfig]);

  const handleDeleteMember = async (underwriterRef) => {
    if (!window.confirm("Remove this underwriter from DMVIC configuration? This action cannot be undone.")) return;

    const originalMembers = [...localMembers];
    setDeletingMemberRef(underwriterRef);
    setLocalMembers((prev) => prev.filter((m) => m.underwriterRef !== underwriterRef));

    try {
      await removeDmvicMember(underwriterRef);
      queryClient.invalidateQueries(["dmvic-config"]);
    } catch (err) {
      console.error("Delete member failed:", err);
      alert("Failed to remove member. Changes reverted.");
      setLocalMembers(originalMembers);
    } finally {
      setDeletingMemberRef(null);
    }
  };

  // ─── Other Queries ─────────────────────────────────────────────
  const {
    data: underwriters = [],
    isLoading: loadingUnderwriters,
    isFetching: fetchingUnderwriters,
    refetch: refetchUnderwriters,
  } = useQuery({
    queryKey: ["configured-underwriters"],
    queryFn: async () => (await fetchConfiguredUnderwriters()).data || [],
    staleTime: 5 * 60 * 1000,
  });

  const { data: myRequests = [], isLoading: loadingRequests, refetch: refetchRequests } = useQuery({
    queryKey: ["my-requests"],
    queryFn: async () => (await listMyRequests()).data || [],
    staleTime: 5 * 60 * 1000,
  });

  const { data: myUnderwriters = [], isLoading: loadingMyUnderwriters } = useQuery({
    queryKey: ["my-underwriters"],
    queryFn: async () => (await listMyUnderwriters()).data || [],
    staleTime: 5 * 60 * 1000,
  });

  // ─── Table Data Preparation ────────────────────────────────────
  const underwriterTableData = underwriters.map((item) => ({
    underwriterName: item.name,
    underwriterSystemRef: item.underwriterSystemRef || item.id || "N/A",
    isActive: item.kycStatus,
    createdAt: item.createdAt || "",
    remarks: item.kycValidationremarks || item.remarks || "—",
  }));

  const activeCount = underwriterTableData.filter((u) => u.isActive === "Yes").length;

  const underwritersFields = [
    { name: "Name" },
    { name: "Status" },
    { name: "Created Date" },
    { name: "Action" },
  ];

  const underwritersRowData = [
    { type: "text", field: "underwriterName" },
    { type: "text", field: "isActive" },
    { type: "date", field: "createdAt" },
    {
      type: "action",
      field: "underwriterSystemRef",
      actions: [{ method: (id) => history.push(`/dashboard/myunderwriterview/${id}`), field: "underwriterSystemRef", title: "View" }],
    },
  ];

  const transformedMyUnderwriters = myUnderwriters.map((u) => ({
    name: u.underwriter?.[0]?.name || "—",
    status: u.status,
    createdAt: u.requestDate,
    remarks: u.approvalRemarks,
    underwriterSystemRef: u.underwriter?.[0]?.underwriterSystemRef || u.underwriter?.[0]?.id, // assuming you need this for action
  }));

  const myUnderwritersFields = [
    { name: "Name" },
    { name: "Status" },
    { name: "Created Date" },
    { name: "Remarks" },
    { name: "Action" },
  ];

  const myUnderwritersRowData = [
    { type: "text", field: "name" },
    { type: "text", field: "status" },
    { type: "date", field: "createdAt" },
    { type: "text", field: "remarks" },
    {
      type: "action",
      field: "underwriterSystemRef",
      actions: [{ method: (id) => history.push(`/dashboard/myunderwriterview/${id}`), field: "underwriterSystemRef", title: "View" }],
    },
  ];

  const transformedRequestData = myRequests.map((request) => ({
    underwriter: request.underwriter?.[0]?.name || "—",
    status: request.status,
    requestDate: request.requestDate,
    requestRemarks: request.approvalRemarks,
  }));

  const requestsFields = [
    { name: "Underwriter" },
    { name: "Status" },
    { name: "Request Date" },
    { name: "Remarks" },
  ];

  const requestsRowData = [
    { type: "text", field: "underwriter" },
    { type: "text", field: "status" },
    { type: "date", field: "requestDate" },
    { type: "text", field: "requestRemarks" },
  ];

  const summaryCards = [
    { title: "Total Underwriters", count: underwriters.length, subtext: "All configured" },
    { title: "Active Underwriters", count: activeCount, subtext: "Currently active" },
  ];

  if (loadingUnderwriters) {
    return <div className="text-center py-5">Loading underwriters data...</div>;
  }

  return (
    <div className="container-fluid py-4">
      {/* Summary Cards */}
      <Row className="g-4 mb-5">
        {summaryCards.map((card, i) => (
          <Col md={6} key={i}>
            <div className="card border-0 shadow-sm h-100" style={{ background: "#003049", color: "white", borderRadius: "12px" }}>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h6 className="mb-1 opacity-75">{card.title}</h6>
                    <h3 className="mb-0">{card.count}</h3>
                  </div>
                  <Users size={32} />
                </div>
                <small className="opacity-75">{card.subtext}</small>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      <Button onClick={() => setModalShow(true)}>Add Underwriter Member</Button>

      {/* DMVIC Configuration Card */}
      <div className="card border-0 shadow-sm mb-5" style={{ borderRadius: "12px" }}>
        <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
          <h5 className="mb-0">DMVIC Configuration</h5>
          <Space>
            {dmvicConfig && (
              <Button variant="outline-primary" size="sm" onClick={() => setCredentialsDrawerOpen(true)}>
                View Credentials
              </Button>
            )}
            <Button
              variant={dmvicConfig ? "outline-primary" : "primary"}
              onClick={() => setDmvicDrawerOpen(true)}
            >
              {dmvicConfig ? "Manage DMVIC" : "Setup DMVIC"}
            </Button>
          </Space>
        </div>

        <div className="card-body p-4">
          {loadingDmvic ? (
            <div className="text-center py-5">Loading DMVIC configuration...</div>
          ) : dmvicConfig ? (
            <>
              {/* Credentials (quick view) */}
              <h6 className="mb-3 fw-bold">Credentials</h6>
              <div className="bg-light p-3 rounded mb-4">
                <div className="mb-2">
                  <strong>Username:</strong> {dmvicConfig.credentials?.username || "—"}
                </div>
                <div>
                  <strong>Client ID:</strong> {dmvicConfig.credentials?.clientId || "—"}
                </div>
              </div>

              {/* Members */}
              <h6 className="mb-3 fw-bold">Members / Underwriters</h6>
              {localMembers.length > 0 ? (
                <Table responsive hover bordered className="mb-4">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Underwriter Ref</th>
                      <th>Member ID</th>
                      <th style={{ width: "140px" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {localMembers.map((member, index) => (
                      <tr key={member.underwriterRef || index}>
                        <td>{index + 1}</td>
                        <td><code className="text-primary">{member.underwriterRef}</code></td>
                        <td>
                          {member?.memberId ? (
                            <Badge bg="info" pill>{member.memberId}</Badge>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteMember(member.underwriterRef)}
                            disabled={deletingMemberRef === member.underwriterRef}
                          >
                            {deletingMemberRef === member.underwriterRef ? "Deleting..." : "Remove"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <Alert variant="info" className="mb-4">
                  No underwriters/members assigned yet.
                </Alert>
              )}

              {/* Issuance Settings */}
              <h6 className="mb-3 fw-bold">Issuance Settings</h6>
              <div className="bg-light p-3 rounded">
                <div className="mb-2">
                  <strong>Validate before issuance:</strong>{" "}
                  <Badge bg={dmvicConfig.issuanceConfig?.validateBeforeIssuance ? "success" : "secondary"}>
                    {dmvicConfig.issuanceConfig?.validateBeforeIssuance ? "Enabled" : "Disabled"}
                  </Badge>
                </div>
                <div className="mb-2">
                  <strong>Auto-confirm risk changes:</strong>{" "}
                  <Badge bg={dmvicConfig.issuanceConfig?.autoConfirmRiskChanges ? "success" : "secondary"}>
                    {dmvicConfig.issuanceConfig?.autoConfirmRiskChanges ? "Enabled" : "Disabled"}
                  </Badge>
                </div>
                <div className="mb-2">
                  <strong>Default Email:</strong> {dmvicConfig.issuanceConfig?.defaultEmail || "—"}
                </div>
                <div>
                  <strong>Default Phone:</strong> {dmvicConfig.issuanceConfig?.defaultPhone || "—"}
                </div>
              </div>
            </>
          ) : (
            <Alert variant="warning">
              No DMVIC configuration found yet. Click "Setup DMVIC" above to begin.
            </Alert>
          )}
        </div>
      </div>

      {/* ─── DMVIC Form Drawer ─── */}
      <Drawer
        title={dmvicConfig ? "Manage DMVIC Configuration" : "Setup DMVIC"}
        placement="right"
        width={600}
        onClose={() => setDmvicDrawerOpen(false)}
        open={dmvicDrawerOpen}
        destroyOnClose
        extra={
          <Space>
            <Button onClick={() => setDmvicDrawerOpen(false)}>Cancel</Button>
            {/* If Dmvic_Credential has no internal save button → add one here */}
          </Space>
        }
      >
        <div style={{ padding: "0 4px" }}>
          <Dmvic_Credential
            refetchDmvic={() => queryClient.invalidateQueries(["dmvic-config"])}
            underwriters={underwriters}
            initialData={dmvicConfig ?? undefined}
            onSuccess={() => {
              setDmvicDrawerOpen(false);
              queryClient.invalidateQueries(["dmvic-config"]);
            }}
            onCancel={() => setDmvicDrawerOpen(false)}
          />

        </div>
      </Drawer>

      {/* Credentials Drawer (optional – keep or remove) */}
      <Drawer
        title="DMVIC Credentials"
        placement="right"
        width={420}
        onClose={() => setCredentialsDrawerOpen(false)}
        open={credentialsDrawerOpen}
      >
        {dmvicConfig?.credentials ? (
          <div>
            <p><strong>Username:</strong> {dmvicConfig.credentials.username || "—"}</p>
            <p><strong>Client ID:</strong> {dmvicConfig.credentials.clientId || "—"}</p>
            {/* Add more fields if available */}
          </div>
        ) : (
          <p>No credentials available.</p>
        )}
      </Drawer>

      {/* ─── Main Underwriters / Requests Card ─── */}
      <div className="card border-0 shadow-sm" style={{ borderRadius: "12px" }}>
        <div className="card-header bg-white border-bottom d-flex justify-content-end gap-3">
          <Button variant="outline-primary" onClick={() => setPanelModalShow(true)}>
            Send Panel Request
          </Button>
          <Button variant="success" onClick={() => setUnderwriterModalShow(true)}>
            Add Underwriter
          </Button>
        </div>

        <div className="card-body p-4">
          <TableComponent
            title="Configured Underwriters"
            table_fields={underwritersFields}
            table_data={underwriterTableData}
            row_data={underwritersRowData}
            isLoading={fetchingUnderwriters}
          />

          <hr className="my-5" style={{ margin: "8px 0" }} />


        </div>


        
      </div>



            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '24px',
              margin: '32px 0',
            }}>
              <div className='card' style={{
                flex: '1 1 500px',           // grow + base width ~500px
                minWidth: '0',
              }}>
              <div 
              style={{
                fontWeight: 600,
                color: 'rgb(0, 31, 95)',
                fontSize: '20px',
                lineHeight: '24px',
                marginBottom: '1.4rem',
              }}   
            className="card-header bg-white border-bottom d-flex justify-content-start gap-3">
              My Requests
                  </div>
                  <div className='card-body'>
                          <TableComponent
                            title=""
                            table_fields={requestsFields}
                            table_data={transformedRequestData}
                            row_data={requestsRowData}
                            isLoading={loadingRequests}
                          />
                  </div>

              </div>

              <div className='card' style={{
                flex: '1 1 500px',
                minWidth: '0',
              }}>
              <div style={{
              fontWeight: 600,
              color: 'rgb(0, 31, 95)',
              fontSize: '20px',
              lineHeight: '24px',
              marginBottom: '1.4rem',
            }}   
            className="card-header bg-white border-bottom d-flex justify-content-start gap-3">
                My Underwriters
               </div>
                      <div className='card-body'>
                   <TableComponent
                  title=""
                  table_fields={myUnderwritersFields}
                  table_data={transformedMyUnderwriters}
                  row_data={myUnderwritersRowData}
                  isLoading={loadingMyUnderwriters}
                />                     
                      </div>

              </div>
            </div>

      {/* Modals */}
      {myUnderwriters.length > 0 && (
        <AddDmvic
          underwriters={underwriters}
          show={modalShow}
          onHide={modalClose}
          fetchDmvics={() => queryClient.invalidateQueries(["dmvic-config"])}
        />
      )}

      <AddProductUnderwriter
        fetchUnderwriters={refetchUnderwriters}
        show={underwriterModalShow}
        onHide={handleUnderwriterModalClose}
      />

      <AddPanelRequest
        fetchUnderwriters={refetchUnderwriters}
        refetchRequests={refetchRequests}
        show={panelModalShow}
        onHide={handlePanelModalClose}
      />
    </div>
  );
}

export default Underwriter;